/**
 * DeviceCommandsCatalog — one-shot commands the uC accepts via
 * cmd/<thing>/request (spec §8.3). Matches the DeviceSettings
 * inspector style: single card, collapsible per-group sections,
 * search across cmd / label, dense hairline-ruled rows.
 *
 * Dispatch is still a placeholder (mirrors DeviceSettings v1 — the
 * write path lands in a separate PR alongside the settings PATCH
 * endpoint). Each Send button shows a tooltip pointing at the
 * follow-up; the row layout / interaction is forward-compatible with
 * a future useSendDeviceCommand mutation.
 */
import { useState } from 'react';
import {
  useDeviceCommands,
  type CommandDescriptor,
  type CommandGroup,
  type CommandParam,
} from '../api/hooks';

const GROUP_LABEL: Record<CommandGroup, string> = {
  power: 'Power',
  audio: 'Audio',
  source: 'Source',
  menu: 'Menu',
  system: 'System',
};

const GROUP_ORDER: CommandGroup[] = ['power', 'audio', 'source', 'menu', 'system'];

function humaniseCmd(cmd: string): string {
  return cmd
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (m) => m.toUpperCase());
}

function commandMatchesSearch(
  c: CommandDescriptor,
  g: CommandGroup,
  q: string,
): boolean {
  if (!q) return true;
  const hay =
    `${g} ${GROUP_LABEL[g]} ${c.cmd} ${humaniseCmd(c.cmd)} ${c.label ?? ''}`.toLowerCase();
  return hay.includes(q.toLowerCase());
}

// ─── Param input (placeholder, disabled until dispatch lands) ────────

function ParamInput({ p }: { p: CommandParam }) {
  const common =
    'text-xs px-2 py-1 border border-border rounded bg-soft text-muted cursor-not-allowed';
  if (p.type === 'bool') {
    return (
      <select disabled className={common} title="Dispatch wired in next iteration">
        <option>true</option>
        <option>false</option>
      </select>
    );
  }
  if (p.type === 'enum' && p.enum) {
    return (
      <select disabled className={common} title="Dispatch wired in next iteration">
        {p.enum.map((e) => (
          <option key={String(e.value)}>{e.label}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      type="number"
      disabled
      min={p.min}
      max={p.max}
      step={p.step ?? 1}
      placeholder={p.optional ? `${p.name} (optional)` : p.name}
      className={`${common} w-24`}
      title="Dispatch wired in next iteration"
    />
  );
}

// ─── Command row ─────────────────────────────────────────────────────

function CommandRow({ c }: { c: CommandDescriptor }) {
  const [expanded, setExpanded] = useState(false);
  const hasParams = (c.params?.length ?? 0) > 0;
  const label = humaniseCmd(c.cmd);
  return (
    <div className="group px-2 py-1.5 border-b border-border last:border-0">
      <div className="flex items-baseline gap-3">
        <div className="flex-1 text-sm text-fg">
          {label}
          {c.label && (
            <span className="ml-2 text-[10px] text-muted">{c.label}</span>
          )}
        </div>
        {hasParams ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs px-2 py-0.5 rounded text-muted hover:text-fg"
            title={
              expanded ? 'Hide parameters' : 'Set parameters and send'
            }
            aria-expanded={expanded}
          >
            {expanded ? '— hide' : 'send →'}
          </button>
        ) : (
          <button
            type="button"
            disabled
            className="text-xs px-2 py-0.5 rounded text-muted bg-soft cursor-not-allowed"
            title="Dispatch wired in next iteration"
          >
            send
          </button>
        )}
      </div>
      {expanded && hasParams && (
        <div className="mt-2 flex flex-wrap items-center gap-2 pl-3">
          {c.params!.map((p) => (
            <ParamInput key={p.name} p={p} />
          ))}
          <button
            type="button"
            disabled
            className="px-3 py-1 rounded-md text-xs font-medium bg-soft text-muted cursor-not-allowed ml-1"
            title="Dispatch wired in next iteration"
          >
            Send
          </button>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="px-2 py-1 rounded-md text-xs text-muted hover:text-fg"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ─── DeviceCommandsCatalog ──────────────────────────────────────────

export function DeviceCommandsCatalog({ thing: _thing }: { thing: string }) {
  const q = useDeviceCommands(_thing);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<CommandGroup, boolean>>(
    {
      power: true,
      audio: true,
      source: true,
      menu: true,
      system: true,
    },
  );
  const toggleGroup = (g: CommandGroup) =>
    setCollapsed((s) => ({ ...s, [g]: !s[g] }));

  if (q.isLoading) {
    return (
      <section className="card">
        <h2 className="text-lg font-semibold tracking-tight mb-3">Commands</h2>
        <p className="text-sm text-muted">Loading commands…</p>
      </section>
    );
  }
  if (q.error) {
    return (
      <section className="card">
        <h2 className="text-lg font-semibold tracking-tight mb-3">Commands</h2>
        <p className="text-sm text-warn">{(q.error as Error).message}</p>
      </section>
    );
  }
  if (!q.data) return null;

  // Group + filter once. Empty groups (after filter) are hidden.
  const filteredByGroup: Array<{
    group: CommandGroup;
    cmds: CommandDescriptor[];
  }> = GROUP_ORDER.map((group) => {
    const cmds = q.data!.catalog
      .filter((c) => c.group === group)
      .filter((c) => commandMatchesSearch(c, group, search));
    return { group, cmds };
  });
  const totalMatches = filteredByGroup.reduce(
    (n, { cmds }) => n + cmds.length,
    0,
  );

  return (
    <section className="card">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <h2 className="text-lg font-semibold tracking-tight">Commands</h2>
        <div className="flex items-center gap-2">
          <input
            type="search"
            placeholder="Search commands…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1.5 text-sm border border-border rounded-md w-56 focus:outline-none focus:border-accent"
          />
          <button
            type="button"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            className="px-2 py-1.5 rounded-md text-sm border border-border bg-white text-muted hover:text-fg disabled:opacity-50 leading-none"
            title={q.isFetching ? 'Refreshing…' : 'Refresh now'}
            aria-label="Refresh commands"
          >
            <span
              className={`inline-block ${q.isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            >
              ↻
            </span>
          </button>
        </div>
      </div>

      <p className="text-[11px] text-muted mb-3">
        Read-only preview — dispatch lands in the next iteration.
      </p>

      {search && totalMatches === 0 && (
        <p className="text-sm text-muted">
          No commands match &ldquo;{search}&rdquo;.
        </p>
      )}

      <div className="space-y-3">
        {filteredByGroup.map(({ group, cmds }) => {
          if (cmds.length === 0) return null;
          const isCollapsed = search ? false : collapsed[group];
          return (
            <div key={group}>
              <button
                type="button"
                onClick={() => toggleGroup(group)}
                className="w-full flex items-baseline gap-2 text-left pb-1 border-b border-border mb-1 hover:text-fg group"
                aria-expanded={!isCollapsed}
                title={
                  isCollapsed
                    ? `Expand ${GROUP_LABEL[group]}`
                    : `Collapse ${GROUP_LABEL[group]}`
                }
              >
                <span
                  aria-hidden="true"
                  className="inline-block w-3 text-center text-[10px] text-muted group-hover:text-fg"
                >
                  {isCollapsed ? '▸' : '▾'}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-muted group-hover:text-fg">
                  {GROUP_LABEL[group]}
                </span>
                <span className="ml-1 text-[10px] text-muted tabular-nums">
                  {cmds.length}
                </span>
              </button>
              {!isCollapsed && (
                <div>
                  {cmds.map((c) => (
                    <CommandRow key={c.cmd} c={c} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
